import React, { memo } from 'react';
import { useStyles } from 'uinix-ui';

import { Input } from '../../types/Input';
import { Nullable } from '../../types/Nullable';
import { Box } from '../Box/Box';

type Props = Input<number> & {
  /** Whether to allow decimal values */
  allowDecimals?: boolean;
  /** Supported UI modes */
  mode?: 'digits';
  /** Max value allowed */
  max?: Nullable<number>;
  /** Min value allowed */
  min?: Nullable<number>;
  /** HTML `step` attribute */
  step?: number | 'any';
};

export const NumberInput = memo(
  ({
    allowDecimals = true,
    error,
    mode,
    onChange,
    step,
    value,
    width,
    ...rest
  }: Props) => {
    const styles = useStyles();
    const effectiveStep = step ?? (allowDecimals ? 'any' : 1);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { validity, value: valueString } = event.target;
      /* An empty sanitized value (valueString === '') is ambiguous: it means
       * either the field was genuinely emptied (deleting the last character,
       * or backspacing "-1" down to a lone sign to allow retyping) or the
       * browser gave up parsing non-numeric text (typed or pasted) and
       * reports it as empty regardless of `validity.badInput`/`valid`.
       * inputType disambiguates: only a deletion is treated as an
       * intentional clear; an insertion that sanitizes to empty is
       * rejected instead of silently committing null. */
      const isDeletion = (
        event.nativeEvent as InputEvent
      ).inputType?.startsWith('delete');
      const isAcceptable = validity.valid || (validity.badInput && isDeletion);
      if (!isAcceptable || (valueString === '' && !isDeletion)) return;

      const numericValue = valueString === '' ? null : Number(valueString);

      if (
        !allowDecimals &&
        numericValue !== null &&
        !Number.isInteger(numericValue)
      ) {
        return;
      }

      onChange(numericValue);
    };

    const handleFocus = (focusEvent: React.FocusEvent<HTMLInputElement>) => {
      focusEvent.target.addEventListener(
        'wheel',
        (wheelEvent: Event) => wheelEvent.preventDefault(),
        { passive: false },
      );
    };

    return (
      <Box
        {...rest}
        as="input"
        step={effectiveStep}
        onFocus={handleFocus}
        styles={[
          styles.input.container,
          mode === 'digits' ? componentStyles.digitsWidth : null,
        ]}
        styleProps={{ error, value, width }}
        type="number"
        value={value === null ? '' : value}
        onChange={handleChange}
      />
    );
  },
);

const componentStyles = {
  digitsWidth: ({ value }: { value: Nullable<number> }) => ({
    minWidth: 'unset',
    paddingRight: 0,
    width: `calc(${(value ?? 1).toString().length}ch + 36px)`,
  }),
};
